import { Box } from '@mui/material';
import { DarkModeOutlined, LightModeOutlined, SettingsBrightnessOutlined } from '@mui/icons-material';
import { useEffect, useState, type ReactNode } from 'react';
import DesignSystem from '../../designSystem';
import {
  getApiLanguage,
  VISIME_LANGUAGE_CHANGED,
  VISIME_UI_LANGUAGE_KEY,
} from '../../config/apiLanguage';
import { useThemeMode } from '../../contexts/ThemeModeContext';
import type { ThemePreference } from '../../config/themeMode';
import { useT } from '../../i18n/useT';

const LANGUAGES: Array<{ code: 'en' | 'de'; label: string }> = [
  { code: 'en', label: 'EN' },
  { code: 'de', label: 'DE' },
];

const APPEARANCE: Array<{
  value: ThemePreference;
  labelKey: 'appearanceSystem' | 'appearanceLight' | 'appearanceDark';
  icon: typeof LightModeOutlined;
}> = [
  { value: 'system', labelKey: 'appearanceSystem', icon: SettingsBrightnessOutlined },
  { value: 'light', labelKey: 'appearanceLight', icon: LightModeOutlined },
  { value: 'dark', labelKey: 'appearanceDark', icon: DarkModeOutlined },
];

function SegmentGroup({
  children,
  ariaLabel,
  dense = false,
}: {
  children: ReactNode;
  ariaLabel: string;
  dense?: boolean;
}) {
  return (
    <Box
      role="group"
      aria-label={ariaLabel}
      sx={{
        display: 'flex',
        gap: dense ? 0.25 : 0.35,
        p: dense ? 0.25 : 0.3,
        borderRadius: DesignSystem.borderRadius.pill,
        backgroundColor: DesignSystem.colors.surface,
        border: `1px solid ${DesignSystem.colors.border}`,
        width: '100%',
      }}
    >
      {children}
    </Box>
  );
}

function SegmentButton({
  active,
  onClick,
  children,
  ariaLabel,
  dense = false,
}: {
  active: boolean;
  onClick: () => void;
  children: ReactNode;
  ariaLabel?: string;
  dense?: boolean;
}) {
  return (
    <Box
      component="button"
      type="button"
      aria-label={ariaLabel}
      aria-pressed={active}
      onClick={onClick}
      sx={{
        flex: 1,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 0.4,
        border: 0,
        py: dense ? 0.35 : 0.45,
        px: 0.5,
        minHeight: dense ? 26 : 28,
        cursor: 'pointer',
        font: 'inherit',
        fontSize: dense ? 11 : 12,
        fontWeight: active ? 800 : 600,
        letterSpacing: '0.02em',
        borderRadius: DesignSystem.borderRadius.pill,
        color: active ? DesignSystem.colors.primary : DesignSystem.colors.textSecondary,
        backgroundColor: active ? DesignSystem.colors.primaryLight : 'transparent',
        boxShadow: active ? DesignSystem.shadows.sm : 'none',
        outline: active ? `1px solid ${DesignSystem.colors.primary}` : '1px solid transparent',
        outlineOffset: -1,
        '&:hover': {
          color: active ? DesignSystem.colors.primary : DesignSystem.colors.textPrimary,
          backgroundColor: active ? DesignSystem.colors.primaryLight : DesignSystem.colors.hover,
        },
        '& svg': { fontSize: dense ? 14 : 15 },
      }}
    >
      {children}
    </Box>
  );
}

export default function LocalePreferenceToggles({
  dense = false,
  showLabels = true,
  appearanceShowLabels,
}: {
  dense?: boolean;
  showLabels?: boolean;
  /** When false, theme segments use icons only (better in narrow sidebars). */
  appearanceShowLabels?: boolean;
}) {
  const showAppearanceLabels = appearanceShowLabels ?? showLabels;
  const t = useT();
  const { preference, setPreference } = useThemeMode();
  const [lang, setLang] = useState(getApiLanguage);

  useEffect(() => {
    const onLang = () => setLang(getApiLanguage());
    window.addEventListener(VISIME_LANGUAGE_CHANGED, onLang);
    return () => window.removeEventListener(VISIME_LANGUAGE_CHANGED, onLang);
  }, []);

  const selectLanguage = (code: 'en' | 'de') => {
    if (code === lang) return;
    localStorage.setItem(VISIME_UI_LANGUAGE_KEY, code);
    window.dispatchEvent(new Event(VISIME_LANGUAGE_CHANGED));
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: dense ? 0.6 : 0.75, width: '100%' }}>
      <SegmentGroup ariaLabel={t('language')} dense={dense}>
        {LANGUAGES.map(({ code, label }) => (
          <SegmentButton
            key={code}
            active={lang === code}
            onClick={() => selectLanguage(code)}
            dense={dense}
          >
            {label}
          </SegmentButton>
        ))}
      </SegmentGroup>

      <SegmentGroup ariaLabel={t('appearance')} dense={dense}>
        {APPEARANCE.map(({ value, labelKey, icon: Icon }) => (
          <SegmentButton
            key={value}
            active={preference === value}
            onClick={() => setPreference(value)}
            ariaLabel={t(labelKey)}
            dense={dense}
          >
            <Icon />
            {showAppearanceLabels && <Box component="span">{t(labelKey)}</Box>}
          </SegmentButton>
        ))}
      </SegmentGroup>
    </Box>
  );
}
